import { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import LinkedInIcon from '@mui/icons-material/LinkedIn';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import SendIcon from '@mui/icons-material/Send';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EventIcon from '@mui/icons-material/CalendarTodayRounded';

import api from "../../services/api";
import { AuthContext } from "../../contexts/auth";

const TONES = ['Professionale', 'Ispirazionale', 'Educativo', 'Storytelling', 'Thought Leadership', 'Casual'];
const AUDIENCES = ['Professionisti B2B', 'Manager e C-level', 'Startup e imprenditori', 'Freelancer', 'HR e Talent', 'Marketing'];
const LANGUAGES = ['Italiano', 'English', 'Español', 'Português', 'Deutsch'];

const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
};

const ModernLinkedInGenerator = () => {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const isSuperuser = user?.is_superuser || false;

    const [topic, setTopic] = useState('');
    const [tone, setTone] = useState('Professionale');
    const [audience, setAudience] = useState('Professionisti B2B');
    const [language, setLanguage] = useState('Italiano');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const [linkedinStatus, setLinkedinStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [connectLoading, setConnectLoading] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [publishResult, setPublishResult] = useState(null);

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [genImgLoading, setGenImgLoading] = useState(false);
    const imageInputRef = useRef(null);

    const [calOpen, setCalOpen] = useState(false);
    const [calDate, setCalDate] = useState(getTomorrow());
    const [calTitle, setCalTitle] = useState('');
    const [calSaving, setCalSaving] = useState(false);

    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => {
        fetchStatus();
        const params = new URLSearchParams(window.location.search);
        if (params.get('linkedin_connected')) {
            const name = params.get('linkedin_name') || '';
            setSnack({ open: true, msg: t('linkedin.snack_connected', { name: name ? ` come ${name}` : '' }), severity: 'success' });
            window.history.replaceState({}, '', window.location.pathname);
            fetchStatus();
        }
        if (params.get('linkedin_error')) {
            const err = params.get('linkedin_error');
            const desc = params.get('linkedin_error_desc') || '';
            const isScope = err && (err.includes('scope') || err.includes('unauthorized_scope'));
            const baseMsg = isScope
                ? t('linkedin.snack_scope_error')
                : t('linkedin.snack_connect_err', { err });
            const msg = desc ? `${baseMsg}: ${desc}` : baseMsg;
            setSnack({ open: true, msg, severity: 'error' });
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchStatus = async () => {
        setStatusLoading(true);
        try {
            const r = await api.get('/api/openai/linkedin/status/');
            setLinkedinStatus(r.data);
        } catch {
            setLinkedinStatus({ connected: false, configured: false });
        }
        setStatusLoading(false);
    };

    const handleConnect = async () => {
        setConnectLoading(true);
        try {
            const r = await api.get('/api/openai/linkedin/auth/');
            window.location.href = r.data.auth_url;
        } catch (err) {
            const msg = err?.response?.data?.error || t('linkedin.snack_connect_fail');
            setSnack({ open: true, msg, severity: 'error' });
        }
        setConnectLoading(false);
    };

    const handleDisconnect = async () => {
        try {
            await api.delete('/api/openai/linkedin/status/');
            setLinkedinStatus(prev => ({ ...prev, connected: false }));
            setSnack({ open: true, msg: t('linkedin.snack_disconnected'), severity: 'info' });
        } catch {
            setSnack({ open: true, msg: t('linkedin.snack_disconnect_err'), severity: 'error' });
        }
    };

    const handleGenerate = async () => {
        if (!topic.trim()) return;
        setLoading(true);
        setResult(null);
        try {
            const resp = await api.post('/api/openai/autopilot/', {
                step: 'linkedin_post',
                payload: { topic, tone, audience, language },
            });
            setResult(resp.data.result);
        } catch (err) {
            if (err?.response?.status === 402) {
                window.dispatchEvent(new CustomEvent('admind_limit_exceeded', { detail: err.response.data || {} }));
                setLoading(false);
                return;
            }
            setResult({ error: err?.response?.data?.error || t('linkedin.error_default') });
        }
        setLoading(false);
    };

    const handleCopy = () => {
        if (result?.post_text) {
            const full = result.post_text + '\n\n' + (result.hashtags || []).map(h => `#${h}`).join(' ');
            navigator.clipboard.writeText(full);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (imageInputRef.current) imageInputRef.current.value = '';
    };

    const handleGenerateImage = async () => {
        if (!topic.trim()) return;
        setGenImgLoading(true);
        try {
            const prompt = `Professional LinkedIn post image about: "${topic}". Tone: ${tone}. Target: ${audience}. Style: corporate, clean, modern, high quality. No text overlay.`;
            const resp = await api.post('/api/openai/dalle-v2/', {
                prompt,
                size: '1792x1024',
                quality: 'high',
            });
            const dataUrl = resp.data.url;
            const fetchResp = await fetch(dataUrl);
            const blob = await fetchResp.blob();
            const file = new File([blob], 'immagine-generata.png', { type: 'image/png' });
            setImageFile(file);
            setImagePreview(dataUrl);
        } catch (_) {
            setSnack({ open: true, msg: t('linkedin.snack_img_err'), severity: 'error' });
        }
        setGenImgLoading(false);
    };

    const handleDownloadImage = () => {
        if (!imagePreview) return;
        const a = document.createElement('a');
        a.href = imagePreview;
        a.download = `linkedin-image-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handlePublish = async () => {
        if (!result?.post_text) return;
        setPublishing(true);
        setPublishResult(null);
        try {
            const text = result.post_text + (result.hashtags?.length ? '\n\n' + result.hashtags.map(h => `#${h}`).join(' ') : '');
            const formData = new FormData();
            formData.append('text', text);
            if (imageFile) formData.append('image', imageFile);
            await api.post('/api/openai/linkedin/publish/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setPublishResult({ success: true });
            setSnack({ open: true, msg: t('linkedin.snack_published'), severity: 'success' });
        } catch (err) {
            const msg = err?.response?.data?.error || t('linkedin.snack_publish_err');
            setSnack({ open: true, msg, severity: 'error' });
        }
        setPublishing(false);
    };

    const handleOpenCal = () => {
        setCalTitle(topic || 'Post LinkedIn');
        setCalDate(getTomorrow());
        setCalOpen(true);
    };

    const handleSaveToCal = async () => {
        if (!calTitle.trim() || !calDate) return;
        setCalSaving(true);
        try {
            const text = result?.post_text ? result.post_text + (result.hashtags?.length ? '\n\n' + result.hashtags.map(h => `#${h}`).join(' ') : '') : '';
            const safeImageUrl = imagePreview && imagePreview.startsWith('http') ? imagePreview : '';
            await api.post('/api/openai/calendar/', {
                title: calTitle.trim(),
                content_type: 'post',
                scheduled_date: calDate,
                content_text: text,
                image_url: safeImageUrl,
                status: 'planned',
            });
            setCalOpen(false);
            setSnack({ open: true, msg: t('linkedin.snack_cal_ok'), severity: 'success' });
        } catch (_) {
            setSnack({ open: true, msg: t('linkedin.snack_cal_err'), severity: 'error' });
        }
        setCalSaving(false);
    };

    const linkedinConnected = linkedinStatus?.connected;

    return (
        <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden', backgroundColor: '#FAFAF8' }}>
            {/* Sidebar */}
            <Box sx={{
                width: 300,
                flexShrink: 0,
                borderRight: '1px solid rgba(0,0,0,0.07)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                backgroundColor: '#F5F4FF',
                p: 2.5,
                gap: 2,
            }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'linear-gradient(135deg, #0A66C2, #2D8CFF)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <LinkedInIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: '#1C1C28', fontSize: '0.95rem' }}>LinkedIn Post</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B' }}>{t('linkedin.subtitle')}</Typography>
                    </Box>
                </Box>

                {/* LinkedIn connection status */}
                <Box sx={{
                    p: 1.5, borderRadius: '12px',
                    background: linkedinConnected ? 'rgba(10,102,194,0.08)' : 'rgba(0,0,0,0.04)',
                    border: `1px solid ${linkedinConnected ? 'rgba(10,102,194,0.2)' : 'rgba(0,0,0,0.08)'}`,
                }}>
                    {statusLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={14} sx={{ color: '#0A66C2' }} />
                            <Typography sx={{ fontSize: '0.78rem', color: '#6B6B7B' }}>{t('linkedin.status_loading')}</Typography>
                        </Box>
                    ) : linkedinConnected ? (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Avatar
                                    src={linkedinStatus.picture}
                                    sx={{ width: 28, height: 28, fontSize: '0.75rem', background: 'linear-gradient(135deg, #0A66C2, #2D8CFF)' }}
                                >
                                    {(linkedinStatus.name || 'U')[0]}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1C1C28', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {linkedinStatus.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.68rem', color: '#6B6B7B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {linkedinStatus.email}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                    label={t('linkedin.status_connected')}
                                    size="small"
                                    sx={{
                                        fontSize: '0.65rem', height: 18,
                                        backgroundColor: 'rgba(16,185,129,0.12)',
                                        color: '#059669',
                                        border: '1px solid rgba(16,185,129,0.3)',
                                        flex: 1,
                                    }}
                                />
                                <Tooltip title={t('linkedin.status_disconnect_tip')}>
                                    <IconButton
                                        size="small" onClick={handleDisconnect}
                                        sx={{ color: '#EF4444', p: 0.3, '&:hover': { background: 'rgba(239,68,68,0.08)' } }}
                                    >
                                        <LinkOffIcon sx={{ fontSize: '0.85rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#6B6B7B', mb: 1 }}>
                                {t('linkedin.status_connect_desc')}
                            </Typography>
                            <Button
                                fullWidth size="small" variant="contained"
                                onClick={handleConnect}
                                disabled={connectLoading}
                                startIcon={connectLoading ? <CircularProgress size={12} color="inherit" /> : <LinkedInIcon />}
                                sx={{
                                    py: 0.8,
                                    background: 'linear-gradient(135deg, #0A66C2, #2D8CFF)',
                                    fontSize: '0.78rem',
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': { background: 'linear-gradient(135deg, #0052A3, #0A66C2)' },
                                }}
                            >
                                {t(connectLoading ? 'linkedin.status_connecting' : 'linkedin.status_connect_btn')}
                            </Button>

                            {/* Admin-only setup instructions */}
                            {isSuperuser && (
                                <Box sx={{ mt: 1.5, p: 1.5, background: 'rgba(112,18,128,0.06)', border: '1px solid rgba(112,18,128,0.2)', borderRadius: '10px' }}>
                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#701280', mb: 0.5 }}>
                                        🔧 Setup (solo admin)
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.68rem', color: '#4B1060', lineHeight: 1.55 }}>
                                        Nel{' '}
                                        <Typography component="a" href="https://developer.linkedin.com/apps" target="_blank" rel="noopener noreferrer"
                                            sx={{ fontSize: '0.68rem', color: '#0A66C2', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                            LinkedIn Developer Portal
                                        </Typography>{' '}
                                        → la tua app → <strong>Products</strong>, attiva:
                                        <br />• <strong>Share on LinkedIn</strong>
                                        <br />• <strong>Sign In with LinkedIn using OpenID Connect</strong>
                                        <br /><br />
                                        Redirect URI autorizzato:<br />
                                        <Typography component="span" sx={{ fontSize: '0.65rem', fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', px: 0.5, py: 0.2, borderRadius: '4px', wordBreak: 'break-all' }}>
                                            https://app.admind.ai/api/openai/linkedin/callback/
                                        </Typography>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Post settings */}
                <TextField
                    fullWidth
                    label={t('linkedin.field_topic')}
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    multiline
                    rows={3}
                    size="small"
                    placeholder={t('linkedin.topic_placeholder')}
                />

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('linkedin.field_tone')}</InputLabel>
                    <Select value={tone} label={t('linkedin.field_tone')} onChange={(e) => setTone(e.target.value)}>
                        {TONES.map((tn, i) => <MenuItem key={tn} value={tn}>{t('linkedin.tones', { returnObjects: true })[i] || tn}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('linkedin.field_audience')}</InputLabel>
                    <Select value={audience} label={t('linkedin.field_audience')} onChange={(e) => setAudience(e.target.value)}>
                        {AUDIENCES.map((a, i) => <MenuItem key={a} value={a}>{t('linkedin.audiences', { returnObjects: true })[i] || a}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('linkedin.field_language')}</InputLabel>
                    <Select value={language} label={t('linkedin.field_language')} onChange={(e) => setLanguage(e.target.value)}>
                        {LANGUAGES.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                </FormControl>

                <Button
                    fullWidth variant="contained"
                    onClick={handleGenerate}
                    disabled={loading || !topic.trim()}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{
                        py: 1.3,
                        background: 'linear-gradient(135deg, #0A66C2, #2D8CFF)',
                        '&:hover': { background: 'linear-gradient(135deg, #0052A3, #0A66C2)', boxShadow: '0 0 20px rgba(10,102,194,0.3)' },
                    }}
                >
                    {t(loading ? 'linkedin.btn_generating' : 'linkedin.btn_generate')}
                </Button>
            </Box>

            {/* Main content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3, display: 'flex', alignItems: result ? 'flex-start' : 'center', justifyContent: 'center', backgroundColor: '#FAFAF8' }}>
                {!result && !loading && (
                    <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            width: 80, height: 80, borderRadius: '22px',
                            background: 'rgba(10,102,194,0.1)',
                            border: '1px solid rgba(10,102,194,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <LinkedInIcon sx={{ fontSize: '2.5rem', color: '#2D8CFF', opacity: 0.5 }} />
                        </Box>
                        <Typography sx={{ color: '#6B6B7B' }}>{t('linkedin.empty_title')}</Typography>
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress sx={{ color: '#0A66C2' }} />
                        <Typography sx={{ color: '#6B6B7B' }}>{t('linkedin.loading_title')}</Typography>
                    </Box>
                )}

                {result?.error && (
                    <Box sx={{ p: 3, borderRadius: '16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                        <Typography sx={{ color: '#EF4444' }}>{result.error}</Typography>
                    </Box>
                )}

                {result && !result.error && (
                    <Box sx={{ width: '100%', maxWidth: 620 }}>
                        {/* Top action bar */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ fontWeight: 600, color: '#1C1C28', fontSize: '0.9rem' }}>{t('linkedin.preview_label')}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={t(copied ? 'linkedin.copy_copied' : 'linkedin.copy_post')}>
                                    <IconButton
                                        onClick={handleCopy}
                                        sx={{ color: copied ? '#10B981' : '#6B6B7B', border: '1px solid rgba(0,0,0,0.10)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(10,102,194,0.3)', background: 'rgba(10,102,194,0.06)' } }}
                                    >
                                        {copied ? <CheckIcon sx={{ fontSize: '1.1rem' }} /> : <ContentCopyIcon sx={{ fontSize: '1.1rem' }} />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('linkedin.regenerate')}>
                                    <IconButton
                                        onClick={handleGenerate}
                                        sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.10)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(10,102,194,0.3)', background: 'rgba(10,102,194,0.06)' } }}
                                    >
                                        <RefreshIcon sx={{ fontSize: '1.1rem' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('linkedin.add_to_cal')}>
                                    <IconButton
                                        onClick={handleOpenCal}
                                        sx={{ color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.25)', borderRadius: '10px', p: 0.8, '&:hover': { borderColor: 'rgba(14,165,233,0.5)', background: 'rgba(14,165,233,0.06)' } }}
                                    >
                                        <EventIcon sx={{ fontSize: '1.1rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Post card */}
                        <Box sx={{ borderRadius: '16px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                            <Box sx={{ p: 2.5 }}>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
                                    <Avatar sx={{
                                        width: 44, height: 44,
                                        background: linkedinConnected && linkedinStatus?.picture ? 'transparent' : 'linear-gradient(135deg, #701280, #FF6B35)',
                                        fontWeight: 700, fontSize: '0.95rem',
                                    }}
                                        src={linkedinConnected ? linkedinStatus?.picture : undefined}
                                    >
                                        {linkedinConnected ? (linkedinStatus?.name || 'U')[0] : 'A'}
                                    </Avatar>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1C1C28' }}>
                                            {linkedinConnected ? linkedinStatus?.name : t('linkedin.your_name')}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#6B6B7B' }}>{audience} • 1° • {t('linkedin.now')}</Typography>
                                    </Box>
                                </Box>

                                <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.75, color: '#4A4A5A', whiteSpace: 'pre-wrap', mb: 1.5 }}>
                                    {result.post_text}
                                </Typography>

                                {result.hashtags && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: imagePreview ? 1.5 : 0 }}>
                                        {result.hashtags.map(h => (
                                            <Typography key={h} sx={{ fontSize: '0.85rem', color: '#2D8CFF', fontWeight: 500 }}>
                                                #{h}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {imagePreview && (
                                    <Box sx={{ mt: 1.5, borderRadius: '8px', overflow: 'hidden' }}>
                                        <img src={imagePreview} alt="immagine post" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Reach estimate */}
                        {result.estimated_reach && (
                            <Box sx={{ mt: 2, p: 2, borderRadius: '12px', background: 'rgba(10,102,194,0.08)', border: '1px solid rgba(10,102,194,0.2)' }}>
                                <Typography sx={{ fontSize: '0.72rem', color: '#6B6B7B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t('linkedin.reach_label')}
                                </Typography>
                                <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#2D8CFF' }}>
                                    {result.estimated_reach}
                                </Typography>
                            </Box>
                        )}

                        {/* Image section */}
                        <Box sx={{ mt: 2 }}>
                            <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />

                            {!imagePreview ? (
                                /* No image: show buttons */
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined" size="small"
                                        startIcon={<ImageIcon />}
                                        onClick={() => imageInputRef.current?.click()}
                                        sx={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6B6B7B', fontSize: '0.8rem', textTransform: 'none', borderRadius: '10px', '&:hover': { borderColor: 'rgba(10,102,194,0.4)', color: '#0A66C2', background: 'rgba(10,102,194,0.04)' } }}
                                    >
                                        {t('linkedin.img_add')}
                                    </Button>
                                    <Button
                                        variant="outlined" size="small"
                                        startIcon={genImgLoading ? <CircularProgress size={14} sx={{ color: '#701280' }} /> : <AutoAwesomeIcon />}
                                        onClick={handleGenerateImage}
                                        disabled={genImgLoading || !topic.trim()}
                                        sx={{ borderColor: 'rgba(112,18,128,0.3)', color: '#701280', fontSize: '0.8rem', textTransform: 'none', borderRadius: '10px', '&:hover': { borderColor: 'rgba(112,18,128,0.5)', background: 'rgba(112,18,128,0.04)' }, '&.Mui-disabled': { opacity: 0.5 } }}
                                    >
                                        {t(genImgLoading ? 'linkedin.img_generating' : 'linkedin.img_generate')}
                                    </Button>
                                </Box>
                            ) : (
                                /* Image present: show action bar */
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Tooltip title={t('linkedin.img_regenerate_tip')}>
                                        <Button
                                            size="small" variant="outlined"
                                            startIcon={genImgLoading ? <CircularProgress size={13} sx={{ color: '#701280' }} /> : <RefreshIcon />}
                                            onClick={handleGenerateImage}
                                            disabled={genImgLoading}
                                            sx={{ borderColor: 'rgba(112,18,128,0.3)', color: '#701280', fontSize: '0.78rem', textTransform: 'none', borderRadius: '10px', '&:hover': { borderColor: 'rgba(112,18,128,0.5)', background: 'rgba(112,18,128,0.04)' } }}
                                        >
                                            {t(genImgLoading ? 'linkedin.img_generating' : 'linkedin.img_regenerate')}
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        size="small" variant="outlined"
                                        startIcon={<ImageIcon />}
                                        onClick={() => imageInputRef.current?.click()}
                                        sx={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6B6B7B', fontSize: '0.78rem', textTransform: 'none', borderRadius: '10px', '&:hover': { borderColor: 'rgba(10,102,194,0.4)', color: '#0A66C2', background: 'rgba(10,102,194,0.04)' } }}
                                    >
                                        {t('linkedin.img_change')}
                                    </Button>
                                    <Tooltip title={t('linkedin.img_download_tip')}>
                                        <IconButton size="small" onClick={handleDownloadImage} sx={{ color: '#10B981', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '8px', p: 0.6, '&:hover': { background: 'rgba(16,185,129,0.06)' } }}>
                                            <DownloadIcon sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('linkedin.img_remove_tip')}>
                                        <IconButton size="small" onClick={handleRemoveImage} sx={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', p: 0.6, '&:hover': { background: 'rgba(239,68,68,0.06)' } }}>
                                            <CloseIcon sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>

                        {/* Publish / Copy button */}
                        <Box sx={{ mt: 2 }}>
                            {linkedinConnected ? (
                                <Button
                                    fullWidth variant="contained"
                                    onClick={handlePublish}
                                    disabled={publishing || publishResult?.success}
                                    startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : publishResult?.success ? <CheckIcon /> : <SendIcon />}
                                    sx={{
                                        py: 1.2,
                                        background: publishResult?.success
                                            ? 'linear-gradient(135deg, #059669, #10B981)'
                                            : 'linear-gradient(135deg, #0A66C2, #2D8CFF)',
                                        '&:hover': {
                                            background: publishResult?.success
                                                ? 'linear-gradient(135deg, #047857, #059669)'
                                                : 'linear-gradient(135deg, #0052A3, #0A66C2)',
                                        },
                                    }}
                                >
                                    {publishing ? t('linkedin.publish_publishing') : publishResult?.success ? t('linkedin.publish_done') : t('linkedin.publish_btn')}
                                </Button>
                            ) : (
                                <Button
                                    fullWidth variant="outlined"
                                    onClick={handleCopy}
                                    startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                                    sx={{
                                        py: 1.2, borderRadius: '12px',
                                        borderColor: 'rgba(0,0,0,0.15)', color: '#4A4A5A',
                                        textTransform: 'none', fontSize: '0.88rem',
                                        '&:hover': { borderColor: 'rgba(0,0,0,0.3)', background: 'rgba(0,0,0,0.02)' },
                                    }}
                                >
                                    {t(copied ? 'linkedin.copy_copied' : 'linkedin.copy_full')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snack.open}
                autoHideDuration={5000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>
                    {snack.msg}
                </Alert>
            </Snackbar>

            {/* Calendar Dialog */}
            <Dialog open={calOpen} onClose={() => !calSaving && setCalOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>{t('linkedin.cal_dialog_title')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        fullWidth label={t('linkedin.cal_field_title')} size="small" value={calTitle}
                        onChange={e => setCalTitle(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                    <TextField
                        fullWidth label={t('linkedin.cal_field_date')} type="date" size="small" value={calDate}
                        onChange={e => setCalDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setCalOpen(false)} disabled={calSaving} sx={{ color: '#6B6B7B', textTransform: 'none' }}>
                        {t('linkedin.cal_cancel')}
                    </Button>
                    <Button
                        variant="contained" onClick={handleSaveToCal}
                        disabled={!calTitle.trim() || !calDate || calSaving}
                        startIcon={calSaving ? <CircularProgress size={14} color="inherit" /> : <EventIcon />}
                        sx={{ textTransform: 'none', borderRadius: '10px', background: 'linear-gradient(135deg, #0EA5E9, #2D8CFF)', '&:hover': { background: 'linear-gradient(135deg, #0284C7, #0A66C2)' } }}
                    >
                        {t(calSaving ? 'linkedin.cal_saving' : 'linkedin.cal_save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModernLinkedInGenerator;
